import { Routes } from '@angular/router';
import { LayoutsComponent } from '../layouts/layouts.component';
import { LoginComponent } from './public/login/login.component';
import { NewPasswordComponent } from './public/new-password/new-password.component';
import { ForgotPasswordComponent } from './public/forgot-password/forgot-password.component';
import { NotFoundComponent } from './public/not-found/not-found.component';
import { UnauthComponent } from './public/unauth/unauth.component';
import { AuthGuard, FreezGuard, LocalStorageGuard, TerminalGuard } from '../auth.guard';
import { ConsentFormComponent } from './misc/consent-form/consent-form.component';
import { SupportInstantComponent } from './public/support-instant/support-instant.component';
import { CommonTermsComponent } from './misc/common-terms/common-terms.component';
import { AgreementComponent } from './misc/agreement/agreement.component';
import { MultioptionFormComponent } from './dashboard/multioption-form/multioption-form.component';
import { RegisterBusinessComponent } from './public/register-business/register-business.component';
import { MigrateComponent } from '../layouts/migrate/migrate.component';
import { DownloadComponent } from './download/download.component';
import { TrainingExportComponent } from './training-export/training-export.component';
import { PaymentReportsComponent } from './reports/payment-reports/payment-reports.component';
import { IframeTllaComponent } from './financing-flow/iframe-tila/iframe-tila.component';
import { VerificationComponent } from './misc/verification/verification.component';
import { MaintenanceComponent } from './public/maintenance/maintenance.component';
import { ScanUploadComponent } from './misc/scan-upload/scan-upload.component';
import { ProjectionCalculatorComponent } from './public/projection-calculator/projection-calculator.component';
import { CustomerLandingPageComponent } from './public/customer-landing-page/customer-landing-page.component';
import { InstantPayoutComponent } from './instant-payout/instant-payout.component';

export const routes: Routes = [
    {
        path: 'app',
        component: LayoutsComponent,
        canActivate: [AuthGuard],
        children: [
            { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
            {
                path: 'dashboard',
                loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule),
                canActivate: [FreezGuard],

            },
            {
                path: 'finance',
                loadChildren: () => import('./financing-flow/financing-flow.module').then(m => m.FinancingFlowModule),
                data: {
                    authGuardRedirect: 'finance',
                },
                canActivate: [FreezGuard, LocalStorageGuard],

            },
            {
                path: 'finance-new',
                loadChildren: () => import('./financing-flow/financing-flow.module').then(m => m.FinancingFlowModule),
                data: {
                    authGuardRedirect: 'finance-new',
                },
                canActivate: [FreezGuard],

            },
            {
                path: 'finance-customer',
                pathMatch: "full",
                component: MultioptionFormComponent,
                canActivate: [FreezGuard]
            },
            {
                path: 'profile',
                loadChildren: () => import('./profile/profile.module').then(m => m.ProfileModule),
                canActivate: [FreezGuard]
            },
            {
                path: 'location',
                loadChildren: () => import('./location/location.module').then(m => m.LocationModule),
                canActivate: [FreezGuard],
            },
            {
                path: 'users',
                loadChildren: () => import('./user/user.module').then(m => m.UserModule),
                canActivate: [FreezGuard],
            },
            {
                path: 'profile',
                loadChildren: () => import('./profile/profile.module').then(m => m.ProfileModule),
                canActivate: [FreezGuard],
            },
            {
                path: 'pricing',
                loadChildren: () => import('./pricing/pricing.module').then(m => m.PricingModule),
                canActivate: [FreezGuard],
            },
            {
                path: 'cancel-subscription',
                loadChildren: () => import('./cancel-flow/cancel-flow.module').then(m => m.CancelFlowModule),
            },
            {
                path: 'global-settings',
                loadChildren: () => import('./global-settings/global-settings.module').then(m => m.GlobalSettingsModule),
                canActivate: [FreezGuard],
            },
            {
                path: 'contract',
                loadChildren: () => import('./contract/contract.module').then(m => m.ContractModule),
                canActivate: [FreezGuard],
            },
            {
                path: 'accounts-receivable',
                loadChildren: () => import('./debt/debt.module').then(m => m.DebtModule),
                canActivate: [FreezGuard],
            },
            {
                path: 'process-payment',
                loadChildren: () => import('./process-payment/process-payment.module').then(m => m.ProcessPaymentModule),
                canActivate: [FreezGuard],

            },
            {
                path: 'subscriptions',
                loadChildren: () => import('./subscriptions/subscriptions.module').then(m => m.SubscriptionsModule),
                canActivate: [FreezGuard],

            },
            {
                path: 'claims',
                loadChildren: () => import('./claim/claim.module').then(m => m.ClaimModule),
                canActivate: [FreezGuard],
            },
            {
                path: 'claims-v2',
                loadChildren: () => import('./claims-v2/claims-v2.module').then(m => m.ClaimsV2Module),
                canActivate: [FreezGuard],
            },
            {
                path: 'help',
                loadChildren: () => import('./help/help.module').then(m => m.HelpModule),
                canActivate: [FreezGuard],
            },
            {
                path: 'reports',
                loadChildren: () => import('./reports/reports.module').then(m => m.ReportsModule),
                canActivate: [FreezGuard]
            },
            {
                path: 'open-api',
                loadChildren: () => import('./open-api/open-api.module').then(m => m.OpenAPIModule),
                canActivate: [FreezGuard],
            },
            {
                path: 'leads',
                loadChildren: () => import('./leads/leads.module').then(m => m.LeadsModule),
                canActivate: [FreezGuard],
            },
            {
                path: 'manage-terminal',
                loadChildren: () => import('./marketing-terminal/marketing-terminal.module').then(m => m.MarketingTerminalModule),
                canActivate: [FreezGuard, TerminalGuard],
            },
            {
                path: 'business-refer',
                loadChildren: () => import('./business-referral/business-referral.module').then(m => m.BusinessReferralModule),
                canActivate: [FreezGuard],

            },
            {
                path: 'customer',
                loadChildren: () => import('./customer-unified/customer-unified.module').then(m => m.CustomerUnifiedModule),
            },
            { path: 'support-instant', component: SupportInstantComponent },
            { path: 'instant-payout', component: InstantPayoutComponent },
            { path: 'agreement', component: AgreementComponent, canActivate: [FreezGuard] },
            { path: '404', component: NotFoundComponent },
            { path: '403', component: UnauthComponent },
            { path: '**', component: NotFoundComponent },
        ]
    },
    { path: 'consentForm', component: ConsentFormComponent },
    { path: 'download', component: DownloadComponent },
    { path: 'trainingExport', component: TrainingExportComponent },
    { path: 'reports', component: PaymentReportsComponent },
    { path: 'agreementForm', component: AgreementComponent },
    { path: 'verification', component: VerificationComponent },
    { path: 'scan-upload', component: ScanUploadComponent },
    { path: 'businessTerms', component: CommonTermsComponent },
    { path: '404', component: NotFoundComponent },
    { path: 'register', component: RegisterBusinessComponent }, 
    { path: 'login', component: LoginComponent },
    { path: 'calculator', component: ProjectionCalculatorComponent },
    { path: 'customer-landing', component: CustomerLandingPageComponent },
    { path: 'maintenance', component: MaintenanceComponent },
    { path: 'migrate', component: MigrateComponent },
    { path: 'initialize', component: MigrateComponent },
    { path: 'forgot-password', component: ForgotPasswordComponent },
    { path: 'new-password', component: NewPasswordComponent },
    { path: 'contractTerms', component: CommonTermsComponent },
    { path: 'paymentProcessTerms', component: CommonTermsComponent },
    { path: 'terms/:term_type', component: CommonTermsComponent },
    { path: 'tila', component: IframeTllaComponent },
    { path: 'instant-payout', component: InstantPayoutComponent},
    { path: '**', component: LoginComponent },
];
